<template>
  <!-- 客户仓库改版页面 -->
  <div class="warehouse-page">
    <!-- 面包屑：订单管理 / 仓库 -->
    <baseBreadcrumb :page="$t('message.warehouse')" :menu="$t('message.orderManagement')" />

    <!-- 页面主体 -->
    <div class="body">
      <!-- 仓库 Tab、筛选和操作按钮 -->
      <div class="warehouse-query">
        <!-- 仓库状态 Tab：数量来自 stockTableNum 接口 -->
        <el-tabs v-model="warehouseTab" type="card" @tab-click="handleWarehouseTabChange">
          <!-- 单个仓库状态 Tab -->
          <el-tab-pane v-for="(tab, index) in warehouseTabs" :key="tab.value" :name="tab.value">
            <!-- Tab 名称及对应数量 -->
            <span slot="label">
              {{ tab.label }}（<span class="tab-count">{{ tabTotals[index].count }}</span>）
            </span>
          </el-tab-pane>
        </el-tabs>

        <!-- 顶部筛选和业务操作区域 -->
        <div v-if="warehouseTab !== 'packing' && warehouseTab !== 'box'" class="filter-bar">
          <!-- 左侧联动筛选条件 -->
          <div class="filter-controls">
            <!-- 商品类型筛选：默认普通商品，该选项不可清空 -->
            <el-select v-model="filters.material_filter" @change="handleMaterialFilterChange">
              <!-- 商品类型固定选项 -->
              <el-option v-for="item in materialFilterOptions" :key="item.value" :label="item.label"
                :value="item.value" />
            </el-select>

            <!-- 搜索字段和搜索关键词组合 -->
            <div class="keyword-filter">
              <!-- 搜索字段选择 -->
              <el-select v-model="filters.search_type" clearable :placeholder="$t('message.warehouseSearchItem')">
                <!-- 可模糊搜索字段 -->
                <el-option v-for="item in searchTypeOptions" :key="item.value" :label="item.label"
                  :value="item.value" />
              </el-select>
              <!-- 搜索关键词输入 -->
              <input v-model.trim="filters.keyword" class="native-input keyword-input" type="search" @keyup.enter="search">
            </div>

            <!-- 排序方式：默认按订单号降序 -->
            <el-select v-model="filters.sort_type">
              <!-- 接口支持的六种排序方式 -->
              <el-option v-for="item in sortOptions" :key="item.value" :label="item.label" :value="item.value" />
            </el-select>

            <!-- 指定辅料筛选：数据源为 admin/material/material/list -->
            <el-select v-model="filters.material_id" clearable filterable :placeholder="$t('message.materialList')"
              @change="handleMaterialChange">
              <!-- 当前客户的辅料选项 -->
              <el-option v-for="item in materialList" :key="item.id" :label="item.name_trans" :value="item.id" />
            </el-select>

            <!-- 按全部筛选条件查询 -->
            <el-button type="primary" class="search-button" @click="search">{{ $t('message.search') }}</el-button>
            <!-- 恢复默认筛选条件 -->
            <el-button @click="resetFilters">{{ $t('message.reset') }}</el-button>
          </div>

          <!-- 右侧业务操作按钮 -->
          <div class="filter-actions">
            <!-- 对已选商品提出售中问题 -->
            <el-button v-if="isAuth(621)" type="danger" class="issue-button" @click="issueSubmissionDuringSale">
              {{ $t('message.issueSubmissionDuringSale') }}
            </el-button>
            <!-- 导出当前筛选条件下的仓库数据 -->
            <el-button type="success" class="export-button" @click="warehouseInvoice">
              {{ $t('message.exportWarehouseData') }}
            </el-button>
          </div>
        </div>
        <!-- 整箱存放专用筛选：仅保留订单号和箱号 -->
        <div v-if="warehouseTab === 'box'" class="filter-bar box-filter-bar">
          <div class="filter-controls">
            <!-- 按订单号模糊搜索整箱记录 -->
            <el-input v-model.trim="filters.box_order_sn" class="box-filter-input" clearable
              :placeholder="$t('message.orderNumber')" @keyup.enter.native="search" />
            <!-- 按箱号模糊搜索整箱记录 -->
            <el-input v-model.trim="filters.box_no" class="box-filter-input" clearable
              :placeholder="$t('message.boxNO')" @keyup.enter.native="search" />
            <!-- 提交整箱筛选条件 -->
            <el-button type="primary" class="search-button" @click="search">{{ $t('message.search') }}</el-button>
            <!-- 重置整箱筛选条件 -->
            <el-button @click="resetFilters">{{ $t('message.reset') }}</el-button>
          </div>
          <!-- 整箱数据导出按钮沿用页面右侧操作位置 -->
          <div class="filter-actions">
            <el-button type="success" class="export-button" @click="warehouseInvoice">导出EXCEL</el-button>
          </div>
        </div>
      </div>

      <!-- 全部仓库列表：首次显示 50 个订单分组，向下滚动后每次追加 50 个 -->
      <div v-if="warehouseTab === 'all' || warehouseTab === 'bulk'" class="warehouse-list">
        <!-- 原 el-loading 改为本页原生遮罩，只显示当前列表加载状态 -->
        <div v-if="listLoading" class="native-loading-mask">
          <span class="native-loading-spinner"></span>
        </div>
        <!-- 列表表头 -->
        <div class="warehouse-grid list-header" :class="{ 'for-sn-group-list': isForSnGrouped }">
          <!-- 全选列 -->
          <div class="select-cell">
            <label class="native-checkbox" :class="{ 'is-checked': allSelected, 'is-indeterminate': allIndeterminate }">
              <input type="checkbox" :checked="allSelected" @change="toggleAll($event.target.checked)">
              <span class="native-checkbox-inner"></span>
            </label>
          </div>
          <!-- 第一层为for_sn分组时，在NO前增加订单号列 -->
          <div v-if="isForSnGrouped">{{ $t('message.orderNumber') }}</div>
          <!-- 商品番号列 -->
          <div>NO</div>
          <!-- 商品照片列 -->
          <div>{{ $t('message.photo') }}</div>
          <!-- 客户下单属性列 -->
          <div>{{ $t('message.customerOrderAttributes') }}</div>
          <!-- 业务编辑属性列 -->
          <div>{{ $t('message.businessEditAttributes') }}</div>
          <!-- 购入数据列 -->
          <div>{{ $t('message.purchaseData') }}</div>
          <!-- 备注栏列 -->
          <div>{{ $t('message.remarksField') }}</div>
          <!-- 业务翻译列 -->
          <div>{{ $t('message.businessTranslation') }}</div>
          <!-- 业务回复列 -->
          <div>{{ $t('message.businessReply') }}</div>
          <!-- 附加服务列 -->
          <div>{{ $t('message.additionalServices') }}</div>
          <!-- 仓库保管信息列 -->
          <div>{{ $t('message.warehouseStorageInformation') }}</div>
          <!-- 库位数量列 -->
          <div>{{ $t('message.numberOfStorageLocations') }}</div>
          <!-- 标签信息列 -->
          <div>{{ $t('message.label') }}</div>
          <!-- 已提出数和可提出数仅在普通/散货列表展示 -->
          <template>
            <div>{{ $t('message.warehouseSubmittedCount') }}</div>
            <div>{{ $t('message.warehouseAvailableCount') }}</div>
          </template>
          <!-- 捆包中列表新增捆包数列 -->
        </div>

        <!-- 接口返回为空时的占位 -->
        <div v-if="!orderList.length && !listLoading" class="empty-list">
          {{ $t('message.warehouseNoData') }}
        </div>

        <!-- 订单分组 -->
        <article v-for="(order, orderIndex) in orderList" :key="getOrderKey(order, orderIndex)" class="order-group">
          <!-- 订单分组头部 -->
          <header class="order-header">
            <!-- 订单选择、展开状态和订单号 -->
            <div class="order-main">
              <!-- 勾选当前订单全部商品 -->
              <label class="native-checkbox" :class="{ 'is-checked': isOrderSelected(order), 'is-indeterminate': isOrderIndeterminate(order) }">
                <input type="checkbox" :checked="isOrderSelected(order)" @change="toggleOrder(order, $event.target.checked)">
                <span class="native-checkbox-inner"></span>
              </label>
              <!-- 展开或收起当前订单 -->
              <button class="native-button collapse-button" type="button" @click="toggleOrderCollapse(getOrderKey(order, orderIndex))">
                {{ isOrderCollapsed(getOrderKey(order, orderIndex)) ? $t('message.warehouseExpand') : $t('message.collapse') }}
                <i :class="isOrderCollapsed(getOrderKey(order, orderIndex)) ? 'el-icon-arrow-down' : 'el-icon-arrow-up'" />
              </button>
              <!-- 有订单号时展示订单号，没有订单号时展示第一层管理番号 -->
              <template v-if="order.order_sn">
                <span>{{ $t('message.orderNumber') }}：</span>
                <router-link class="order-link" :to="'/orderPaymentDetail?order_sn=' + order.order_sn" target="_blank">
                  {{ order.order_sn }}
                </router-link>
                <!-- 复制订单号 -->
                <i class="el-icon-copy-document copy-icon" @click="copy(order.order_sn)" />
              </template>
              <template v-else>
                <span>{{ $t('message.managementDesignation') }}：</span>
                <span class="order-link">{{ displayValue(order.for_sn) }}</span>
              </template>
            </div>

            <!-- 订单管理备注：双击进入编辑 -->
            <div v-if="order.order_sn" class="order-follow-remark" @dblclick="startOrderFollowRemarkEdit(order)">
              <span class="order-meta-label">{{ $t('message.warehouseOrderRemark') }}：</span>
              <!-- 备注编辑输入框 -->
              <input v-if="editingRemarkOrderSn === order.order_sn" v-model="order.follow_remark"
                class="native-input order-remark-input" autofocus @blur="saveOrderFollowRemark(order)"
                @keyup.enter="saveOrderFollowRemark(order)">
              <!-- 备注只读文本 -->
              <span v-else class="editable-remark">
                {{ displayValue(order.follow_remark) }}
              </span>
            </div>

            <!-- 子账号信息 -->
            <div v-if="order.children_user && order.children_user.length" class="children-users">
              <span class="order-meta-label">{{ $t('message.warehouseSubaccount') }}：</span>
              <!-- 单个下单子账号 -->
              <span v-for="user in order.children_user" :key="user.id">
                {{ user.children_username }}
              </span>
            </div>

            <!-- 订单剩余免费保管天数 -->
            <div v-if="order.order_sn" class="free-days">
              {{ $t('message.warehouseRemainingFreeDays') }}：
              <strong>{{ displayValue(order.days_surplus_for_free) }}</strong>
            </div>
          </header>

          <!-- 当前订单商品列表 -->
          <div v-show="!isOrderCollapsed(getOrderKey(order, orderIndex))" class="order-details">
            <!-- 单个仓库商品及其辅料模块，真实容器负责提供稳定 key -->
            <div v-for="detail in order.order_detail" :key="detail.id" class="detail-block">
              <!-- 当前商品为辅料时，标识固定在当前商品列表块左上角 -->
              <WarehouseMaterialMarkTip v-if="detail.material_id > 0" class="current-material-mark"
                :material-id="detail.material_id" :user-id="$route.query.userID"
                :required-quantity="detail.shipment_num || detail.send_may_num || 0" />
              <!-- 商品主数据行 -->
              <div class="warehouse-grid detail-row" :class="{ 'for-sn-group-list': isForSnGrouped }">
                <!-- 商品选择 -->
                <div class="select-cell">
                  <label class="native-checkbox" :class="{ 'is-checked': isDetailSelected(detail.id) }">
                    <input type="checkbox" :checked="isDetailSelected(detail.id)" @change="toggleDetail(detail.id, $event.target.checked)">
                    <span class="native-checkbox-inner"></span>
                  </label>
                  <!-- 只有普通商品已绑定辅料时才显示下方辅料展开按钮 -->
                  <button v-if="detail.has_material_bind == 1 && detail.material_id == 0"
                    class="native-button material-toggle-button"
                    type="button" @click="toggleDetailMaterial(detail)">
                    {{ isDetailMaterialExpanded(detail.id) ? $t('message.collapse') : $t('message.warehouseExpand') }}
                    <i :class="isDetailMaterialExpanded(detail.id) ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
                  </button>
                </div>

                <!-- 第一层为for_sn分组时，商品行增加订单号列 -->
                <div v-if="isForSnGrouped" class="detail-order-sn-cell">
                  <!-- 商品行订单号点击后跳转订单详情 -->
                  <router-link v-if="detail.order_sn" class="detail-order-sn-link"
                    :to="'/orderPaymentDetail?order_sn=' + detail.order_sn" target="_blank">
                    {{ detail.order_sn }}
                  </router-link>
                  <span v-else>{{ displayValue(detail.order_sn) }}</span>
                </div>

                <!-- 商品番号、管理番号和禁运提示 -->
                <div class="number-cell">
                  <!-- 禁运商品标记 -->
                  <div v-if="detail.forbid_goods != false && detail.forbid_goods != null" class="contraband">
                    <!-- 图标颜色与订单详情保持一致：status 为 3 时使用已处理图标 -->
                    <img v-if="detail.forbid_goods.status == 3" src="@/assets/img/contraband1.png"
                      class="contraband-icon" alt="禁运商品">
                    <img v-else src="@/assets/img/contraband.png" class="contraband-icon" alt="禁运商品">
                    <!-- 鼠标移入图标后展示违禁品类型 -->
                    <div class="contraband-popover">{{ detail.forbid_goods.type_name }}</div>
                  </div>
                  <!-- 订单内商品番号 -->
                  <strong>{{ displayValue(detail.sorting) }}</strong>
                  <!-- 商品管理番号：沿用其他列表的579权限控制编辑能力 -->
                  <input v-if="isAuth(579)" v-model="detail.for_sn" class="native-input for-sn-input"
                    @blur="saveDetailForSn(detail)" @keyup.enter="$event.target.blur()">
                  <!-- 无管理番号编辑权限时仅展示只读内容 -->
                  <span v-else class="for-sn">{{ displayValue(detail.for_sn) }}</span>
                </div>

                <!-- 商品或辅料照片 -->
                <div class="photo-cell">
                  <el-image class="product-image" :src="getDetailImage(detail)" fit="cover"
                    :preview-src-list="getPreviewImages(detail)">
                    <!-- 图片加载失败占位 -->
                    <div slot="error" class="image-placeholder">
                      <i class="el-icon-picture-outline" />
                    </div>
                  </el-image>
                </div>

                <!-- 客户下单属性：直接展示 order_detail.detail -->
                <div class="attribute-cell">
                  <!-- 单条客户下单属性 -->
                  <p v-for="(attribute, attributeIndex) in detail.detail"
                    :key="'detail-' + detail.id + '-' + attributeIndex">
                    <span>{{ attribute.key }}：</span>
                    <span>{{ attribute.value }}</span>
                  </p>
                </div>

                <!-- 业务编辑属性：直接展示 order_detail.detail_y -->
                <div class="attribute-cell">
                  <!-- 单条业务编辑属性 -->
                  <p v-for="(attribute, attributeIndex) in detail.detail_y"
                    :key="'detail-y-' + detail.id + '-' + attributeIndex">
                    <span>{{ attribute.key }}：</span>
                    <span>{{ attribute.value }}</span>
                  </p>
                </div>

                <!-- 购入数据：字段及小计规则沿用原仓库列表 -->
                <div class="purchase-cell">
                  <!-- 普通商品实例返回购入数据时才展示，避免为辅料构造默认值 -->
                  <template v-if="detail.confirm_num !== undefined">
                    <p>{{ $t('message.quantity') }}：{{ detail.confirm_num }}</p>
                    <p>{{ $t('message.unitPrice') }}：{{ detail.confirm_price }}</p>
                    <p>{{ $t('message.domesticFreight') }}：{{ detail.confirm_freight }}</p>
                    <p>
                      {{ $t('message.subtotal') }}：{{ (detail.confirm_num * detail.confirm_price - 0 +
                        (detail.confirm_freight - 0)).toFixed(2) }}
                    </p>
                  </template>
                </div>

                <!-- 客户备注 -->
                <div class="warehouse-textarea-cell remark-cell">
                  <!-- 客户备注使用与业务回复一致的只读文本域 -->
                  <textarea class="native-textarea" :value="detail.client_remark" rows="4" readonly></textarea>
                </div>

                <!-- 业务翻译：当前 order_detail 实例未返回对应字段 -->
                <div class="warehouse-textarea-cell translation-cell">
                  <!-- 实例未返回翻译字段，仅保留与业务回复一致的空白只读文本域 -->
                  <textarea class="native-textarea" value="" rows="4" readonly></textarea>
                </div>

                <!-- 业务回复：失焦后保存 -->
                <div class="warehouse-textarea-cell reply-cell">
                  <!-- 业务回复编辑框 -->
                  <textarea v-model="detail.y_reply" class="native-textarea" rows="4" :disabled="!isAuth(582)"
                    @blur="saveBusinessReply(detail)"></textarea>
                </div>

                <!-- 附加服务：直接展示 order_detail.option -->
                <div class="service-cell">
                  <!-- 存在附加服务时，鼠标移入裁切区域展示完整列表 -->
                  <div v-if="detail.option && detail.option.length" class="native-popover">
                    <!-- 浮层中的完整附加服务列表 -->
                    <div class="service-popover-content">
                      <p v-for="(option, optionIndex) in detail.option"
                        :key="'option-popover-' + detail.id + '-' + optionIndex">
                        {{ optionIndex + 1 }}. {{ option.name }} * {{ option.num }}
                      </p>
                    </div>
                    <!-- 表格内最多显示 150px 高度，超出内容隐藏 -->
                    <div class="service-list">
                      <p v-for="(option, optionIndex) in detail.option"
                        :key="'option-' + detail.id + '-' + optionIndex">
                        {{ optionIndex + 1 }}. {{ option.name }} * {{ option.num }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- 仓库保管信息：展示接口返回的入库时间和剩余免费保管天数 -->
                <div class="storage-cell">
                  <!-- 入库时间：沿用仓库列表真实返回的 first_upstorage_at 字段 -->
                  <p>{{ $t('message.timeOfStorage') }}：</p>
                  <p> <span style="display: inline-block; width: 80px">{{ detail.first_upstorage_at }}</span> </p>
                  <!-- 保管日数 days_surplus_for_free 字段 -->
                  <p>{{ $t('message.warehouseStorageDays') }}：<br>{{ detail.days_used_for_free }}天</p>
                </div>
                <!-- 库位编号截取“】-”后的部分，并显示对应数量 -->
                <div class="stock-grid-cell">
                  <template v-if="detail.stock_grids && detail.stock_grids.length">
                    <p v-for="(stockGrid, stockGridIndex) in detail.stock_grids" :key="stockGridIndex">
                      {{ getStockGridNumber(stockGrid.grid_number) }} ({{ stockGrid.num }})
                    </p>
                  </template>
                  <span v-else>-</span>
                </div>
                <!-- 标签信息：实例明确返回 type、no 和 goods_no -->
                <div class="tag-cell">
                  <!-- 标签较多时只显示单元格内容，鼠标移入后展示完整标签 -->
                  <div v-if="detail.order_detail_tag && detail.order_detail_tag.length" class="native-tag-popover">
                    <div class="tag-popover-content">
                      <div v-for="(tag, tagIndex) in detail.order_detail_tag"
                        :key="'tag-popover-' + detail.id + '-' + tagIndex" class="tag-item">
                        <span>{{ tag.type }}</span>
                        <span>{{ $t('message.warehouseTagNo') }}：{{ tag.no }}</span>
                        <span>{{ $t('message.warehouseGoodsCode') }}：{{ tag.goods_no }}</span>
                      </div>
                    </div>
                    <div class="tag-list">
                      <div v-for="(tag, tagIndex) in detail.order_detail_tag"
                        :key="'tag-' + detail.id + '-' + tagIndex" class="tag-item">
                        <span>{{ tag.type }}</span>
                        <span>{{ $t('message.warehouseTagNo') }}：{{ tag.no }}</span>
                        <span>{{ $t('message.warehouseGoodsCode') }}：{{ tag.goods_no }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 已提出数、可提出数仅在普通/散货列表展示 -->
                <template>
                  <div class="quantity-cell">{{ displayValue(detail.send_success_num) }}</div>
                  <div class="quantity-cell available-quantity-cell">
                    <!-- 点击可提出数打开库位查看弹框 -->
                    <span
                      class="available-quantity-value"
                      @click="openStorageLocation(detail)"
                    >{{ displayValue(detail.send_may_num) }}</span>
                    <!-- 出货数默认预填可提出数，只允许输入大于0的整数且不超过可提出数 -->
                    <input v-model="detail.shipment_num" class="native-input shipment-quantity-input"
                      type="text" :placeholder="$t('message.shipmentQuantity')"
                      :disabled="Number(detail.send_may_num || 0) <= 0"
                      @input="sanitizeShipmentQuantity(detail)" @blur="validateShipmentQuantity(detail)"
                      @keyup.enter="$event.target.blur()">
                  </div>
                </template>
                <!-- 捆包中列表展示接口返回的捆包数 -->
              </div>

              <!-- 普通商品绑定的辅料默认收起，点击按钮后展示接口返回的辅料卡片 -->
              <WarehouseMaterialCard
                v-if="detail.has_material_bind == 1 && detail.material_id == 0 && isDetailMaterialExpanded(detail.id)"
                class="warehouse-material-panel" :list="getDetailMaterials(detail)"
                :order-quantity="detail.send_may_num" />
            </div>
          </div>
        </article>
      </div>

      <!-- 其他 Tab 的列表将在后续阶段实现 -->
      <!-- 捆包中使用独立列表组件，不复用普通仓库列表的筛选、勾选和底部操作 -->
      <WarehousePackingList v-else-if="warehouseTab === 'packing'" :orders="orderList" :loading="listLoading" />
      <!-- 整箱存放使用独立箱子列表组件 -->
      <WarehouseBoxList
        v-else-if="warehouseTab === 'box'"
        :boxes="orderList"
        :loading="listLoading"
        @submit="handleBoxDeliverySubmit"
      />

      <div v-else class="pending-list">
        {{ $t('message.warehouseTabPending') }}
      </div>
      <!-- 粘性定位的底部批量操作栏，与仓库列表保持同宽 -->
    <div v-if="warehouseTab === 'all' || warehouseTab === 'bulk'" class="warehouse-bottom-bar">
      <!-- 底部全选与已选统计 -->
      <div class="bottom-selection-summary">
        <label class="native-checkbox native-checkbox-with-label"
          :class="{ 'is-checked': allSelected, 'is-indeterminate': allIndeterminate }">
          <input type="checkbox" :checked="allSelected" @change="toggleAll($event.target.checked)">
          <span class="native-checkbox-inner"></span>
          <span>{{ $t('message.warehouseSelectAll') }}</span>
        </label>
        <span class="selected-summary-label">{{ $t('message.selected') }}：</span>
        <span class="selected-summary-value">{{ selectedSummary.count }}{{ $t('message.jianShu') }}</span>
        <span class="selected-summary-value">{{ selectedSummary.points }}{{ $t('message.warehousePoints') }}</span>
        <span class="selected-summary-value">{{ selectedSummary.amount }}{{ $t('message.yuan') }}</span>
      </div>

      <!-- 底部配送单操作 -->
      <div class="bottom-operation-actions">
        <!-- 自动计算辅料开关，交由配送单页面继续处理 -->
        <label class="native-checkbox native-checkbox-with-label"
          :class="{ 'is-checked': autoCalculateMaterial }">
          <input v-model="autoCalculateMaterial" type="checkbox">
          <span class="native-checkbox-inner"></span>
          <span>{{ $t('message.warehouseAutoCalculateMaterial') }}</span>
        </label>
        <!-- 使用已选商品进入配送单提出页面 -->
        <button class="native-button native-button-danger bottom-submit-button" type="button" :disabled="!selectedDetailIds.length"
          @click="putForwardDeliveryOrder">
          {{ $t('message.putForwardDeliveryOrder') }}
        </button>
      </div>
    </div>
    </div>

    <!-- 售中问题提出弹窗 -->
    <inSalePreview ref="inSalePreview" />
    <!-- 库位查看弹框：点击可提出数打开 -->
    <storage-location-products ref="storageLocationProducts" />
    <!-- 当前仓库勾选商品的配送单预览弹框 -->
    <WarehouseDeliveryPreview ref="warehouseDeliveryPreview" @confirm="handleDeliveryPreviewConfirm" />
    <!-- 整箱生成配送单成功后的结果弹框 -->
    <div v-if="boxDeliveryResultVisible" class="native-dialog-mask">
      <div class="native-dialog box-delivery-result-dialog">
        <div class="native-dialog-header">
          <span class="native-dialog-title">配送单生成成功</span>
          <button class="native-dialog-close" type="button" @click="boxDeliveryResultVisible = false">×</button>
        </div>
        <!-- 接口返回的配送单号支持复制并可在新窗口打开详情 -->
        <div class="box-delivery-result">
          <span>配送单号：</span>
          <a class="box-delivery-link" href="#" @click.prevent="openBoxDeliveryDetail">
            {{ boxDeliveryOrderNo }}
          </a>
          <button class="native-button native-button-mini" type="button" @click="copyBoxDeliveryOrderNo">复制</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 售中问题提出公共组件
import inSalePreview from '@/components/inSale/inSalePreview'
// 当前仓库页面专用的辅料卡片组件
// 当前仓库页面专用的辅料卡片组件
import WarehouseMaterialCard from './componens/warehouseMaterialCard'
// 当前仓库专用的配送单预览弹框
import WarehouseDeliveryPreview from './componens/warehouseDeliveryPreview'
// 捆包中专用列表组件
// 捆包中专用列表组件
import WarehousePackingList from './componens/warehousePackingList'
// 整箱存放专用列表组件
import WarehouseBoxList from './componens/warehouseBoxList'
// 当前仓库页面专用的辅料标识组件
import WarehouseMaterialMarkTip from './componens/warehouseMaterialMarkTip'
// 库位查看弹框（与检品详情共用）
import storageLocationProducts from '@/views/inspectDetail/components/storageLocationProducts'

export default {
  // 当前页面使用的业务组件
  components: {
    inSalePreview,
    WarehouseMaterialCard,
    WarehouseDeliveryPreview,
    WarehousePackingList,
    WarehouseBoxList,
    WarehouseMaterialMarkTip,
    storageLocationProducts,
  },
  data() {
    return {
      warehouseTab: 'all', // 当前选中的仓库 Tab
      lastWarehouseTab: 'all', // 上一次选中的仓库 Tab，用于判断是否真正切换
      // 仓库 Tab 配置，顺序与数量接口返回顺序一致
      warehouseTabs: [
        { label: this.$t('message.all'), value: 'all' },
        { label: this.$t('message.warehouseBulkStorage'), value: 'bulk' },
        { label: this.$t('message.warehouseBoxStorage'), value: 'box' },
        { label: this.$t('message.kunbaozhong'), value: 'packing' },
      ],
      tabTotals: [{ count: 0 }, { count: 0 }, { count: 0 }, { count: 0 }], // 各 Tab 数量
      // 顶部筛选表单，字段名与仓库接口保持一致
      filters: {
        material_filter: 0, // 商品类型：0=全部，1=普通商品，2=辅料
        search_type: '', // 当前模糊搜索字段
        keyword: '', // 当前模糊搜索关键词
        sort_type: 2, // 排序方式：默认订单号降序
        material_id: '', // 指定辅料 ID
        box_order_sn: '', // 整箱订单号
        box_no: '', // 整箱箱号
      },
      // 商品类型固定选项
      materialFilterOptions: [
        { label: this.$t('message.warehouseNormalGoods'), value: 1 },
        { label: this.$t('message.warehouseMaterialGoods'), value: 2 },
        { label: this.$t('message.all'), value: 0 },
      ],
      // 条件搜索字段
      searchTypeOptions: [
        { label: this.$t('message.managementDesignation'), value: 'for_sn' },
        { label: this.$t('message.customerRemarks'), value: 'client_remark' },
        { label: this.$t('message.businessReply'), value: 'y_reply' },
        { label: this.$t('message.warehouseTagNo'), value: 'tag_no' },
        { label: this.$t('message.warehouseGoodsCode'), value: 'goods_no' },
        { label: this.$t('message.orderNumber'), value: 'order_sn' },
      ],
      // 仓库排序选项，3/4 由接口按 for_sn 重新分组返回
      sortOptions: [
        { label: this.$t('message.warehouseOrderSnAsc'), value: 1 },
        { label: this.$t('message.warehouseOrderSnDesc'), value: 2 },
        { label: this.$t('message.warehouseForSnAsc'), value: 3 },
        { label: this.$t('message.warehouseForSnDesc'), value: 4 },
        { label: this.$t('message.warehouseStorageDaysAsc'), value: 5 },
        { label: this.$t('message.warehouseStorageDaysDesc'), value: 6 },
      ],
      materialList: [], // 当前客户的辅料下拉选项
      materialBindMap: {}, // 按 order_detail_id 保存商品已绑定辅料列表
      materialBindRequestedIds: [], // 已请求辅料绑定信息的商品 ID，避免滚动时重复请求
      expandedMaterialDetailIds: [], // 当前已展开辅料区域的商品 ID，默认全部收起
      orderList: [], // client.stockAutoList 返回的完整订单列表
      selectedDetailIds: [], // 当前勾选的订单商品 ID
      collapsedOrderSnList: [], // 当前已收起的订单号
      editingRemarkOrderSn: '', // 当前正在编辑管理备注的订单号
      followRemarkSnapshot: '', // 编辑前的订单管理备注
      autoCalculateMaterial: true, // 底部配送单操作是否自动计算辅料
      listLoading: false, // 全部列表加载状态
      exportLoading: null, // 仓库导出加载实例
      // 控制整箱生成配送单成功后的结果弹框
      boxDeliveryResultVisible: false,
      // 保存orderBoxToPorder接口返回的配送单号
      boxDeliveryOrderNo: '',
    }
  },
  computed: {
    // 判断接口是否返回第一层for_sn分组结构
    isForSnGrouped() {
      const firstOrder = this.orderList[0]
      return Boolean(firstOrder && !firstOrder.order_sn && firstOrder.for_sn !== undefined)
    },
    // 当前列表全部商品 ID
    allDetailIds() {
      return this.orderList.reduce((ids, order) => {
        return ids.concat(order.order_detail.map(detail => detail.id))
      }, [])
    },
    // 是否已经勾选当前列表全部商品
    allSelected() {
      return Boolean(this.allDetailIds.length) &&
        this.allDetailIds.every(id => this.selectedDetailIds.includes(id))
    },
    // 是否只勾选了当前列表部分商品
    allIndeterminate() {
      const selectedCount = this.allDetailIds.filter(id => this.selectedDetailIds.includes(id)).length
      return selectedCount > 0 && selectedCount < this.allDetailIds.length
    },
    // 底部栏显示勾选番数、出货总数量和购入总价
    selectedSummary() {
      const details = this.orderList.reduce((list, order) => list.concat(order.order_detail || []), [])
        .filter(detail => this.selectedDetailIds.includes(detail.id))
      const quantity = details.reduce((total, detail) => total + Number(detail.shipment_num || 0), 0)
      const amount = details.reduce((total, detail) => {
        // 总价按购入数据的小计计算：购入数量×单价＋国内运费
        return total + Number(detail.confirm_num || 0) * Number(detail.confirm_price || 0) + Number(detail.confirm_freight || 0)
      }, 0)
      return {
        count: details.length,
        points: quantity,
        amount: amount.toFixed(2),
      }
    },
  },
  created() {
    // 初始化顶部 Tab 数量
    this.stockTableNum()
    // 初始化当前客户辅料下拉列表
    this.materialListSearch()
    // 初始化“全部”仓库列表
    this.stockAutoList()
  },
  methods: {
    // 获取仓库 Tab 数量
    stockTableNum() {
      this.$api.clienteleApi.stockTableNum(this.getWarehouseFilterParams()).then(res => {
        this.tabTotals = res.data
      })
    },
    // 获取当前客户辅料列表
    materialListSearch() {
      this.$api.warehouseApi.materialList({
        user_id: this.$route.query.userID,
        page: 1,
        pageSize: 999,
      }).then(res => {
        // 辅料分页数据位于 data.list 或 data.data
        const data = res.data || {}
        this.materialList = data.list || data.data || []
      })
    },
    // 商品类型切换为普通商品或全部时清空指定辅料
    handleMaterialFilterChange(value) {
      if (value !== 2) this.filters.material_id = ''
    },
    // 选择指定辅料时自动切换到辅料商品
    handleMaterialChange(value) {
      if (value) this.filters.material_filter = 2
    },
    // 组装全部仓库筛选参数
    getWarehouseFilterParams() {
      // 所有请求均限定当前客户
      const params = { user_id: this.$route.query.userID }
      // 商品类型始终传参；指定辅料时强制联动 material_filter=2
      params.material_filter = this.filters.material_id ? 2 : this.filters.material_filter
      if (this.filters.material_id) params.material_id = this.filters.material_id
      // 将模糊搜索关键词写入选中的接口字段
      if (this.filters.search_type && this.filters.keyword) {
        params[this.filters.search_type] = this.filters.keyword
      }
      // 排序字段始终按数值枚举传递
      params.sort_type = this.filters.sort_type
      return params
    },
    // 按当前全部筛选条件刷新数量和列表
    search() {
      this.stockTableNum()
      this.loadCurrentWarehouseList()
    },
    // 筛选默认值：普通商品、订单号降序，搜索项为空
    createDefaultFilters() {
      return {
        material_filter: 0,
        search_type: '',
        keyword: '',
        sort_type: 2,
        material_id: '',
        box_order_sn: '',
        box_no: '',
      }
    },
    // 重置筛选并恢复普通商品和订单号降序
    resetFilters() {
      this.filters = this.createDefaultFilters()
      // 重置后立即按默认条件查询
      this.search()
    },
    // 获取“全部”仓库列表
    stockAutoList() {
      this.requestWarehouseListPaged('stockAutoList')
    },
    // 获取散货存放列表
    stockBulkList() {
      this.requestWarehouseListPaged('stockBulkList')
    },
    // 获取捆包中列表
    encasementListV3() {
      this.requestWarehouseListPaged('encasementListV3')
    },
    // 整箱存放列表接口
    completedOrderBoxList() {
      this.requestWarehouseListPaged('completedOrderBoxList')
    },
    // 根据当前Tab选择对应的仓库接口
    loadCurrentWarehouseList() {
      if (this.warehouseTab === 'bulk') {
        this.stockBulkList()
        return
      }
      if (this.warehouseTab === 'packing') {
        this.encasementListV3()
        return
      }
      if (this.warehouseTab === 'box') {
        this.completedOrderBoxList()
        return
      }
      this.stockAutoList()
    },
    // 切换仓库Tab时清空搜索项，再按默认条件刷新对应列表
    handleWarehouseTabChange() {
      if (this.warehouseTab === this.lastWarehouseTab) {
        this.loadCurrentWarehouseList()
        return
      }
      this.lastWarehouseTab = this.warehouseTab
      this.resetFilters()
    },
    // 分页加载仓库列表，当前页完成后自动继续请求下一页
    requestWarehouseListPaged(apiName) {
      this.listLoading = true
      this.orderList = []
      this.materialBindMap = {}
      this.materialBindRequestedIds = []
      this.expandedMaterialDetailIds = []
      this.selectedDetailIds = []

      // 捆包中只传客户和排序；整箱列表使用订单号和箱号筛选
      const baseParams = apiName === 'encasementListV3'
        ? { user_id: this.$route.query.userID, sort_type: this.filters.sort_type }
        : apiName === 'completedOrderBoxList'
          ? {
            user_id: this.$route.query.userID,
            order_sn: this.filters.box_order_sn,
            box_no: this.filters.box_no,
          }
          : this.getWarehouseFilterParams()
      const pageSize = apiName === 'completedOrderBoxList' ? 15 : 50

      const loadPage = page => {
        const params = {
          ...baseParams,
          page,
          pageSize,
        }
        return this.$api.clienteleApi[apiName](params).then(res => {
          if (res.code !== 0) {
            this.$message.error(res.msg)
            return
          }

          // 分页响应使用 data.data；旧接口直接返回数组时只处理当前响应
          const responseData = res.data || {}
          const rawOrders = Array.isArray(responseData)
            ? responseData
            : (Array.isArray(responseData.data) ? responseData.data : [])

          // 保留接口订单和商品两层结构，并预填可提出数量
          const currentOrders = rawOrders.map(order => ({
            ...order,
            order_detail: (order.order_detail || order.list || []).map(detail => ({
              ...(detail.order_detail || {}),
              ...detail,
              id: (detail.order_detail && detail.order_detail.id) || detail.id || detail.order_detail_id,
              packing_count: detail.num !== undefined
                ? detail.num
                : (detail.order_detail && detail.order_detail.packing_quantity),
              shipment_num: detail.shipment_num === undefined
                ? (Number(detail.send_may_num || 0) > 0 ? Number(detail.send_may_num) : '')
                : detail.shipment_num,
            })),
          }))
          this.orderList = this.orderList.concat(currentOrders)

          // 优先使用接口最后页；没有分页信息时按总数或当前页数量判断
          const lastPage = Number(responseData.lastPage || responseData.last_page || 0)
          const total = Number(responseData.total || 0)
          const hasNextPage = !Array.isArray(responseData) && (
            lastPage > page ||
            (!lastPage && total > this.orderList.length) ||
            (!lastPage && !total && rawOrders.length >= pageSize)
          )
          if (hasNextPage) return loadPage(page + 1)
        })
      }

      return loadPage(1).finally(() => {
        this.listLoading = false
      })
    },

    // 缺失字段统一展示短横线，数字 0 保持显示
    displayValue(value) {
      return value === undefined || value === null || value === '' ? '-' : value
    },
    // 库位仅显示“】-”后的实际编号。
    getStockGridNumber(gridNumber) {
      if (!gridNumber) return ''
      const separatorIndex = gridNumber.indexOf('】-')
      return separatorIndex === -1 ? gridNumber : gridNumber.slice(separatorIndex + 2)
    },
    // 点击可提出数：打开库位查看弹框（入参取 order_purchase[].id，多个用逗号拼接）
    openStorageLocation(detail) {
      const purchases = Array.isArray(detail.order_purchase) ? detail.order_purchase : []
      const order_purchase_id = purchases
        .map(item => item && item.id)
        .filter(id => id !== undefined && id !== null && id !== '')
        .join(',')
      if (!order_purchase_id) return
      this.$refs.storageLocationProducts.stockListCharge(order_purchase_id)
    },
    // 输入出货数时仅保留数字，并即时限制为不超过可提出数
    sanitizeShipmentQuantity(detail) {
      const digits = String(detail.shipment_num === undefined || detail.shipment_num === null
        ? '' : detail.shipment_num).replace(/[^\d]/g, '')
      let value = digits === '' ? '' : parseInt(digits, 10)
      const max = Number(detail.send_may_num || 0)
      if (value !== '' && value > max) value = max
      if (value === 0) value = ''
      this.$set(detail, 'shipment_num', value)
    },
    // 出货数失焦时保证最终值为大于0的整数
    validateShipmentQuantity(detail) {
      this.sanitizeShipmentQuantity(detail)
      const max = Number(detail.send_may_num || 0)
      const value = Number(detail.shipment_num)
      if ((!Number.isInteger(value) || value <= 0) && max > 0) {
        this.$set(detail, 'shipment_num', max)
      }
    },
    // 商品图片：辅料优先使用 material.main_image
    getDetailImage(detail) {
      
      return detail.pic
    },
    // 图片预览仅在存在图片地址时启用
    getPreviewImages(detail) {
      const image = this.getDetailImage(detail)
      return image ? [image] : []
    },
    // 判断当前商品的辅料区域是否已经展开
    isDetailMaterialExpanded(detailId) {
      return this.expandedMaterialDetailIds.includes(detailId)
    },
    // 展开或收起单个商品的辅料区域
    toggleDetailMaterial(detail) {
      // 当前商品本身为辅料或未绑定辅料时不允许展开下方模块
      if (detail.material_id > 0 || detail.has_material_bind != 1) return
      if (this.isDetailMaterialExpanded(detail.id)) {
        this.expandedMaterialDetailIds = this.expandedMaterialDetailIds.filter(id => id !== detail.id)
        return
      }
      this.expandedMaterialDetailIds = this.expandedMaterialDetailIds.concat(detail.id)
      // 普通商品首次展开时请求其已绑定辅料
      this.loadDetailMaterialBindList(detail)
    },
    // 加载单个普通商品已绑定的辅料列表
    loadDetailMaterialBindList(detail) {
      if (this.materialBindRequestedIds.includes(detail.id)) return
      this.materialBindRequestedIds.push(detail.id)
      this.$api.clienteleApi.stockMaterialBindList({
        user_id: this.$route.query.userID,
        order_detail_ids: [detail.id],
        max_ids: 50,
      }).then(res => {
        // 接口返回 order_detail_id + material_bind，取当前商品对应的绑定数组
        const group = (res.data || []).find(item => String(item.order_detail_id) === String(detail.id))
        this.$set(this.materialBindMap, detail.id, group ? group.material_bind : [])
      })
    },
    // 获取商品行下方辅料卡片使用的数据
    getDetailMaterials(detail) {
      // 普通商品使用 stockMaterialBindList 返回的绑定列表
      if (this.materialBindMap[detail.id]) return this.materialBindMap[detail.id]
      // 绑定辅料接口返回前保持空卡片状态
      return []
    },
    // 判断单个商品是否被勾选
    isDetailSelected(detailId) {
      return this.selectedDetailIds.includes(detailId)
    },
    // 切换单个商品勾选状态
    toggleDetail(detailId, selected) {
      if (selected) {
        this.selectedDetailIds = [...new Set(this.selectedDetailIds.concat(detailId))]
        return
      }
      this.selectedDetailIds = this.selectedDetailIds.filter(id => id !== detailId)
    },
    // 获取当前订单的全部商品 ID
    getOrderDetailIds(order) {
      return order.order_detail.map(detail => detail.id)
    },
    // 判断当前订单是否全部勾选
    isOrderSelected(order) {
      const ids = this.getOrderDetailIds(order)
      return Boolean(ids.length) && ids.every(id => this.selectedDetailIds.includes(id))
    },
    // 判断当前订单是否部分勾选
    isOrderIndeterminate(order) {
      const ids = this.getOrderDetailIds(order)
      const selectedCount = ids.filter(id => this.selectedDetailIds.includes(id)).length
      return selectedCount > 0 && selectedCount < ids.length
    },
    // 切换当前订单全部商品勾选状态
    toggleOrder(order, selected) {
      const ids = this.getOrderDetailIds(order)
      if (selected) {
        this.selectedDetailIds = [...new Set(this.selectedDetailIds.concat(ids))]
        return
      }
      this.selectedDetailIds = this.selectedDetailIds.filter(id => !ids.includes(id))
    },
    // 切换当前列表全部商品勾选状态
    toggleAll(selected) {
      this.selectedDetailIds = selected ? this.allDetailIds.slice() : []
    },
    // 兼容第一层没有订单号的管理番号分组，生成稳定的折叠和列表 key
    getOrderKey(order, index) {
      return order.order_sn || ('for-sn-' + (order.for_sn || 'empty') + '-' + index)
    },
    // 判断订单是否处于收起状态
    isOrderCollapsed(orderSn) {
      return this.collapsedOrderSnList.includes(orderSn)
    },
    // 展开或收起当前订单
    toggleOrderCollapse(orderSn) {
      if (this.isOrderCollapsed(orderSn)) {
        this.collapsedOrderSnList = this.collapsedOrderSnList.filter(item => item !== orderSn)
        return
      }
      this.collapsedOrderSnList = this.collapsedOrderSnList.concat(orderSn)
    },
    // 双击订单管理备注后进入编辑状态
    startOrderFollowRemarkEdit(order) {
      this.editingRemarkOrderSn = order.order_sn
      this.followRemarkSnapshot = order.follow_remark || ''
    },
    // 调用 order.followRemarkEdit 保存订单管理备注
    saveOrderFollowRemark(order) {
      const currentRemark = order.follow_remark || ''
      // 内容未变化时直接退出编辑
      if (currentRemark === this.followRemarkSnapshot) {
        this.editingRemarkOrderSn = ''
        return
      }
      this.$api.orderApi.followRemarkEdit({
        order_sn: order.order_sn,
        follow_remark: currentRemark,
      }).then(res => {
        this.followRemarkSnapshot = currentRemark
        this.editingRemarkOrderSn = ''
        this.$message.success(res.msg)
      })
    },
    // 保存单番商品管理番号，接口参数使用单个订单详情ID数组
    saveDetailForSn(detail) {
      this.$api.orderApi.batchUpdateOrderDetailForSn({
        order_detail_id_set: [detail.id],
        for_sn: detail.for_sn || '',
      })
    },
    // 售中问题提出成功后的父页面回调：刷新列表和 Tab 数量
    problemStoreSuccess() {
      this.stockTableNum()
      this.loadCurrentWarehouseList()
    },
    // 保存单番商品业务回复
    saveBusinessReply(detail) {
      this.$api.clienteleApi.serviceRemarkUpdate({
        order_detail_id: detail.id,
        y_reply: detail.y_reply,
      })
    },
    // 对当前勾选商品提出售中问题
    issueSubmissionDuringSale() {
      if (!this.selectedDetailIds.length) {
        this.$message.error(this.$t('message.selectableOperational'))
        return
      }
      this.$refs.inSalePreview.storePreview(this.selectedDetailIds)
    },
    // 底部栏提出配送单：携带当前勾选的商品进入配送单预览页
    putForwardDeliveryOrder() {
      if (!this.selectedDetailIds.length) {
        this.$message.error(this.$t('message.selectableOperational'))
        return
      }
      const details = this.orderList.reduce((list, order) => list.concat(order.order_detail || []), [])
        .filter(detail => this.selectedDetailIds.includes(detail.id))
      // 勾选自动计算辅料时，不允许只勾选辅料标识商品提出配送单
      if (this.autoCalculateMaterial && details.length && details.every(detail => detail.material_id > 0)) {
        this.$message.error('勾选自动计算辅料，单独辅料标识的不允许提出配送单')
        return
      }
      // 预览弹框从当前列表读取商品并自动加载绑定辅料详情
      // 只有勾选了“自动计算辅料”，并且商品存在辅料绑定时才进入辅料预览弹框
      // 未勾选时按普通订单提出，不读取、不携带辅料列表数据
      const hasMaterial = details.some(detail => detail.has_material_bind == 1)
      if (this.autoCalculateMaterial && hasMaterial) {
        this.$refs.warehouseDeliveryPreview.open(details, this.$route.query.userID)
        return
      }
      // 未启用自动计算辅料或没有辅料时，直接跳转普通配送单提出页面
      const stock = details.map(detail => ({
        // 携带列表中编辑后的出货数，作为配送单预览的预填数量
        order_detail_id: detail.id,
        prefill_num: Number(detail.shipment_num || detail.send_may_num || 0),
      }))
      const route = this.$router.resolve({
        path: '/deliveryPreview',
        query: {
          id: this.selectedDetailIds.join(','),
          userID: this.$route.query.userID,
          stock: encodeURIComponent(JSON.stringify(stock)),
        },
      })
      window.open(route.href, '_blank')
    },
    // 预览确认后携带完整 stock 参数跳转配送单页面
    handleDeliveryPreviewConfirm(stock) {
      const route = this.$router.resolve({
        path: '/deliveryPreview',
        query: {
          userID: this.$route.query.userID,
          stock: encodeURIComponent(JSON.stringify(stock)),
        },
      })
      window.open(route.href, '_blank')
    },
    // 按当前筛选条件导出仓库数据
    // 提交已选整箱生成配送单，成功后刷新列表
    handleBoxDeliverySubmit(boxIds) {
      if (!boxIds || !boxIds.length) {
        this.$message.error(this.$t('message.selectableOperational'))
        return
      }
      this.listLoading = true
      this.$api.clienteleApi.orderBoxToPorder({
        user_id: this.$route.query.userID,
        boxIds,
      }).then(res => {
        if (res.code !== 0) {
          this.$message.error(res.msg)
          return
        }
        // 刷新列表前展示接口返回的配送单号
        this.boxDeliveryOrderNo = String(res.data || '')
        this.boxDeliveryResultVisible = true
        this.$message.success(res.msg || '配送单生成成功')
        this.stockTableNum()
        this.completedOrderBoxList()
      }).finally(() => {
        this.listLoading = false
      })
    },
    // 在浏览器新窗口打开生成的配送单详情
    openBoxDeliveryDetail() {
      if (!this.boxDeliveryOrderNo) return
      const route = this.$router.resolve({
        path: '/porderDetail',
        query: { porder_sn: this.boxDeliveryOrderNo },
      })
      window.open(route.href, '_blank')
    },
    // 将生成的配送单号复制到剪贴板
    copyBoxDeliveryOrderNo() {
      if (!this.boxDeliveryOrderNo) return
      const text = this.boxDeliveryOrderNo
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('复制成功')
        })
        return
      }
      const input = document.createElement('textarea')
      input.value = text
      input.setAttribute('readonly', '')
      input.style.position = 'fixed'
      input.style.opacity = '0'
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
      this.$message.success('复制成功')
    },
    warehouseInvoice() {
      // 打开导出加载状态
      this.exportLoading = this.$loading({
        lock: true,
        text: this.$t('message.warehouseExporting'),
        spinner: 'el-icon-loading fontSize50',
        background: 'rgba(255, 255, 255, 0.7)',
      })
      // 创建仓库导出请求
      const xhr = new XMLHttpRequest()
      xhr.open('post', this.baseURL + '/client.warehouseInvoice', true)
      xhr.responseType = 'blob'
      xhr.setRequestHeader('accept', '*/*')
      xhr.setRequestHeader('accept-language', 'zh-CN')
      xhr.setRequestHeader('Content-type', 'application/x-www-form-urlencoded')
      // 导出接口使用后台登录令牌
      const userToken = localStorage.getItem('japan_access_token')
      xhr.setRequestHeader('admintoken', 'Bearer ' + userToken)
      // 导出完成后下载文件并关闭加载状态
      xhr.onload = () => {
        if (xhr.status === 200 && xhr.response.type !== 'text/html') {
          const link = document.createElement('a')
          link.href = window.URL.createObjectURL(xhr.response)
          link.download = 'warehouse-export.xlsx'
          link.click()
          window.URL.revokeObjectURL(link.href)
        } else {
          this.$message.error(this.$t('message.warehouseExportFailed'))
        }
        this.exportLoading.close()
      }
      // 网络错误时关闭加载状态并提示
      xhr.onerror = () => {
        this.exportLoading.close()
        this.$message.error(this.$t('message.warehouseExportFailed'))
      }
      // 将当前联动筛选条件编码为表单请求体
      const params = this.getWarehouseFilterParams()
      const requestBody = Object.keys(params)
        .map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key]))
        .join('&')
      xhr.send(requestBody)
    },
  },
}
</script>

<style lang="scss" scoped>
// 客户仓库页面基础布局
.warehouse-page {

  min-width: 0;
  padding-bottom: 2px;

  // 允许页面主体跟随可用宽度收缩
  .body {
    min-width: 0;
  }
}

// 仓库列表底部粘性批量操作栏，与列表内容保持同宽
.warehouse-bottom-bar {
  position: sticky;
  bottom: 0;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 62px;
  margin-top: 12px;
  padding: 10px 18px;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.97);
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  box-sizing: border-box;
}

// 底部栏左侧统计信息
.bottom-selection-summary,
.bottom-operation-actions {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 14px;
}

// 已选统计标签与数值颜色
.selected-summary-label {
  color: #606266;
}

.selected-summary-value {
  color: #c7252b;
  white-space: nowrap;
}

// 当前页原生按钮，模拟 Element 按钮的尺寸、边框和交互
.native-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 20px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  box-sizing: border-box;
  appearance: none;
  -webkit-appearance: none;

  &:hover {
    color: #c7252b;
    border-color: #f0c4c6;
    background: #fff6f6;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
}

.native-button-primary,
.native-button-danger {
  color: #fff;
  background: #bd2429;
  border-color: #bd2429;

  &:hover {
    color: #fff;
    background: #c7252b;
    border-color: #c7252b;
  }
}

.native-button-success {
  color: #fff;
  background: #0aae45;
  border-color: #0aae45;

  &:hover {
    color: #fff;
    background: #13b850;
    border-color: #13b850;
  }
}

.native-button-mini {
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
}

// 当前页原生输入框，模拟 Element input
.native-input {
  width: 100%;
  height: 40px;
  padding: 0 15px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  background: #fff;
  color: #606266;
  font-size: 14px;
  line-height: 40px;
  outline: none;
  box-sizing: border-box;

  &:focus {
    border-color: #c7252b;
  }

  &:disabled {
    cursor: not-allowed;
    background: #f5f7fa;
    color: #c0c4cc;
  }
}

// 当前页原生复选框，模拟 Element checkbox 的勾选和半选状态
.native-checkbox {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  line-height: 1;
  cursor: pointer;

  input {
    position: absolute;
    width: 100%;
    height: 100%;
    margin: 0;
    opacity: 0;
    cursor: pointer;
  }
}

.native-checkbox-with-label {
  width: auto;
  gap: 6px;
  color: #606266;
}

.native-checkbox-inner {
  position: relative;
  width: 18px;
  height: 18px;
  border: 1px solid #dcdfe6;
  border-radius: 2px;
  background: #fff;
  box-sizing: border-box;
}

.native-checkbox.is-checked .native-checkbox-inner,
.native-checkbox.is-indeterminate .native-checkbox-inner {
  border-color: #bd2429;
  background: #bd2429;
}

.native-checkbox.is-checked .native-checkbox-inner::after {
  content: "";
  position: absolute;
  left: 6px;
  top: 2px;
  width: 5px;
  height: 10px;
  border: solid #fff;
  border-width: 0 1px 1px 0;
  transform: rotate(45deg);
}

.native-checkbox.is-indeterminate .native-checkbox-inner::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 8px;
  width: 8px;
  height: 1px;
  background: #fff;
}

// 底部栏提交按钮样式
.bottom-submit-button {
  min-width: 96px;
}

// 顶部 Tab 和筛选区域
.warehouse-query {
  margin-bottom: 16px;

  // 缩小 Tab 与筛选栏之间的间距
  /deep/ .el-tabs__header {
    margin-bottom: 14px;
  }

  // 统一 Tab 最小宽度和文字对齐
  /deep/ .el-tabs__item {
    min-width: 118px;
    text-align: center;
  }

  // 激活 Tab 和数量使用主题红色
  /deep/ .el-tabs__item.is-active,
  .tab-count {
    color: #c7252b;
  }
}

// 顶部筛选栏左右布局
.filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

// 左侧筛选控件布局
.filter-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;

  // 普通下拉框统一宽度
  >.el-select {
    width: 190px;
  }

  // 商品类型下拉框宽度
  >.el-select:first-child {
    width: 150px;
  }

  // 搜索字段和关键词组合
  .keyword-filter {
    display: flex;

    // 搜索字段下拉框宽度
    .el-select {
      width: 120px;
    }

    // 搜索关键词输入框宽度
    .keyword-input {
      width: 210px;
      margin-left: -1px;
      border-radius: 0 4px 4px 0;
    }

    // 组合控件左侧圆角
    /deep/ .el-select .el-input__inner {
      border-radius: 4px 0 0 4px;
    }

  }

  // 搜索按钮与筛选框保留间距
  .search-button {
    margin-left: 2px;
  }

  // 整箱筛选输入框固定宽度，避免被原生 search 样式撑开
  .box-filter-input {
    width: 190px;
  }
}

// 右侧业务按钮布局
.filter-actions {
  display: flex;
  flex-shrink: 0;

  // 售中问题按钮与导出按钮保持间距
  .issue-button {
    margin-right: 10px;
  }
}

// 全部列表使用页面可用宽度，不产生横向滚动条
.warehouse-list {
  position: relative;
  width: 100%;
  min-height: 240px;
  min-width: 0;
  // 不设置 overflow，避免生成新的滚动容器导致表头 sticky 失效
  overflow: visible;
  color: #333;
}

// 当前页原生列表加载遮罩，替代 el-loading
.native-loading-mask {
  position: absolute;
  inset: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.65);
}

.native-loading-spinner {
  width: 28px;
  height: 28px;
  border: 3px solid #ebeef5;
  border-top-color: #bd2429;
  border-radius: 50%;
  animation: warehouse-loading-rotate 0.8s linear infinite;
}

@keyframes warehouse-loading-rotate {
  to {
    transform: rotate(360deg);
  }
}

// 表头和商品行共用的百分比弹性列结构
.warehouse-grid {
  display: grid;
  width: 100%;
  min-width: 0;
  // 普通列表共15列，复选框列缩窄，附加服务列加宽
  grid-template-columns: 0.55fr 0.85fr 1fr 1.2fr 1.2fr 1.1fr 1.15fr 1.15fr 1.15fr 1.75fr 1.1fr 1.1fr 0.9fr 0.7fr 0.9fr;
}

// 第一层for_sn分组增加订单号列后的16列比例
.warehouse-grid.for-sn-group-list {
  grid-template-columns: 0.55fr 0.95fr 0.85fr 1fr 1.2fr 1.2fr 1.1fr 1.15fr 1.15fr 1.15fr 1.75fr 1.1fr 1.1fr 0.9fr 0.7fr 0.9fr;
}

// 捆包中移除勾选、已提出数和可提出数后使用12列布局
.warehouse-grid.packing-list {
  grid-template-columns: 5% 6% 7% 12% 12% 10% 10% 10% 8% 10% 5% 5%;
}

// 管理番号分组中的订单号单元格
.detail-order-sn-cell {
  color: #c7252b;
  font-size: 12px;
  text-align: center;
  word-break: break-all;
}

// 新增订单号列沿用订单详情链接样式
.detail-order-sn-link {
  color: #c7252b;
  text-decoration: underline;
}

// 列表表头
.list-header {
  position: sticky;
  top: 0;
  z-index: 99;
  align-items: center;
  min-height: 48px;
  background: #eef0f5;
  border: 1px solid #e4e7ed;
  color: #606266;
  font-size: 14px;
  font-weight: 600;
  text-align: center;

  // 表头单元格内边距
  >div {
    min-width: 0;
    padding: 8px 4px;
    box-sizing: border-box;
  }
}

// 单个订单分组
.order-group {
  width: 100%;
  min-width: 0;
  margin-bottom: 0;
  border: 1px solid #ebeef5;
  border-top: 0;
  border-radius: 0;
  overflow: visible;
}

// 订单分组头部：按参考图保持单行四区布局
.order-header {
  display: grid;
  // 订单号按完整内容自适应，备注区域使用剩余空间
  grid-template-columns: max-content minmax(0, 1fr) max-content max-content;
  align-items: center;
  column-gap: 20px;
  width: 100%;
  min-width: 0;
  min-height: 42px;
  padding: 6px 26px;
  box-sizing: border-box;
  background: #56576a;
  color: #fff;
  font-size: 13px;
  overflow: hidden;
}

// 订单选择和订单号区域
.order-main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: max-content;
  white-space: nowrap;

  // 深色背景下的复选框边框
  .native-checkbox-inner {
    border-color: #dcdfe6;
  }
}

// 展开收起按钮
.collapse-button {
  height: 20px;
  margin: 0 60px 0 10px;
  padding: 1px 6px;
  border-color: #e4e7ed;
  color: #606266;
  font-size: 11px;
  line-height: 16px;
}


// 订单详情链接
.order-link {
  flex: none;
  color: #f4da45;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
}

// 订单号复制图标
.copy-icon {
  cursor: pointer;
}

// 订单备注编辑区域
.order-follow-remark {
  display: flex;
  align-items: center;
  min-width: 0;
  overflow: hidden;

  // 备注输入框占满剩余宽度
  .order-remark-input {
    flex: 1;
  }
}

// 订单头部字段名称
.order-meta-label {
  flex-shrink: 0;
  color: #d7d8df;
}

// 可双击编辑的订单备注
.editable-remark {
  display: block;
  min-width: 0;
  min-height: 20px;
  overflow: hidden;
  cursor: text;
  text-overflow: ellipsis;
  white-space: nowrap;
}

// 子账号信息
.children-users {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  white-space: nowrap;
}

// 剩余免费保管天数
.free-days {
  grid-column: 4;
  justify-self: start;
  white-space: nowrap;

  // 剩余天数强调色
  strong {
    color: #f4da45;
  }
}

// 单个商品数据行
.detail-block {
  position: relative;
  width: 100%;
  min-width: 0;
  overflow: visible;
  border-bottom: 1px solid #ebeef5;
}

// 商品主数据行
.detail-row {
  position: relative;
  align-items: stretch;
  min-height: 94px;
  border-bottom: 1px solid #ebeef5;
  background: #fff;

  // 最后一个商品不重复绘制底边
  &:last-child {
    border-bottom: 0;
  }

  // 商品行所有单元格
  >div {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: center;
    padding: 10px 6px;
    box-sizing: border-box;
    // order-details 商品内容统一使用 14px 字号
    font-size: 14px;
    overflow: hidden;
    overflow-wrap: anywhere;
  }
}

// 选择列
.select-cell {
  text-align: center;
}

// 商品行的复选框和辅料展开按钮上下排列
.detail-row>.select-cell {
  flex-direction: column;
  gap: 10px;
}

// 辅料展开和收起按钮保持紧凑，避免撑宽选择列
.material-toggle-button {
  height: 22px;
  margin-left: 0;
  padding: 2px 5px;
  font-size: 11px;
  line-height: 16px;
}

// 商品番号列
.number-cell {
  position: relative;
  z-index: 50;
  flex-direction: column;
  gap: 8px;
  // 允许违禁品悬浮说明超出番号列完整显示
  overflow: visible !important;
}

// 当前商品为辅料时使用蓝色标识，鼠标移入由公共组件展示详情
.current-material-mark {
  // 标识脱离编号内容布局，固定在当前商品列表块左上角
  position: absolute;
  top: 8px;
  left: 8px;
  z-index: 2;

  /deep/ .material-mark {
    background-color: #ecf5ff;
    color: #409eff;
  }
}

// 管理番号编辑输入框：跟随编号列宽度，不写死输入框宽度
.for-sn-input {
  width: 100%;
  padding: 0 6px;
  box-sizing: border-box;
  text-align: center;
}

// 无权限时的管理番号只读展示样式
.for-sn {
  width: 100%;
  padding: 4px 2px;
  border: 1px solid #dcdfe6;
  border-radius: 3px;
  box-sizing: border-box;
  text-align: center;
}

// 禁运商品标记：图标和悬浮详情沿用订单详情展示方式
.contraband {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.contraband-icon {
  display: block;
  width: 30px;
  height: 30px;
  object-fit: contain;
  cursor: pointer;
}

.contraband-popover {
  position: absolute;
  left: calc(100% + 8px);
  top: 50%;
  z-index: 40;
  display: none;
  width: 200px;
  padding: 10px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  color: #303133;
  font-size: 14px;
  line-height: 1.5;
  transform: translateY(-50%);
  box-sizing: border-box;
}

.contraband:hover .contraband-popover {
  display: block;
}

// 商品照片单元格
.photo-cell {
  padding: 8px 4px !important;
}

// 商品图片
.product-image {
  width: 60px;
  max-width: 100%;
  height: 60px;
  border-radius: 0;
  background: #f5f7fa;
}

// 图片加载失败占位
.image-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #c0c4cc;
  font-size: 24px;
}

// 客户和业务属性单元格
.attribute-cell {
  display: flex;
  flex-direction: column;
  // 客户属性和业务属性与购入数据保持一致，内容统一靠左
  align-items: flex-start !important;
  min-width: 0;
  text-align: left;

  // 属性数组逐行展示真实键值
  p {
    margin: 0;
    line-height: 1.45;
  }
}

// 购入数据和保管信息单元格
.purchase-cell,
.storage-cell,
.stock-grid-cell {
  flex-direction: column;
  align-items: flex-start !important;
  line-height: 1.45;

  // 数据项使用紧凑行距，匹配参考列表密度
  p {
    margin: 0;
  }
}

// 库位编号和对应数量使用蓝色突出显示。
.stock-grid-cell {
  color: #409eff;
}

// 客户备注单元格
.remark-cell {
  white-space: pre-wrap;
}

// 备注、业务翻译和业务回复统一使用业务回复文本域样式
.warehouse-textarea-cell {
  // 三列文本域统一占满单元格
  .native-textarea {
    width: 100%;
    height: 100px;
    min-height: 100px !important;
    padding: 8px;
    border: 1px solid #dcdfe6;
    border-radius: 4px;
    color: #606266;
    font-size: 14px;
    line-height: 1.4;
    outline: none;
    resize: none;
    box-sizing: border-box;

    &:focus {
      border-color: #c7252b;
    }

    &:disabled,
    &[readonly] {
      background: #f5f7fa;
      color: #606266;
    }
  }
}

// 附加服务单元格
.service-cell {
  position: relative;
  z-index: 10;
  flex-direction: column;
  align-items: flex-start !important;
  overflow: visible !important;

  // 表格中的附加服务最高显示 150px，超出部分裁切
  .service-list {
    width: 100%;
    max-height: 150px;
    overflow: hidden;
  }

  // 附加服务逐条紧凑展示
  p {
    margin: 0;
    line-height: 1.45;
  }
}

// 当前页原生 hover 浮层，替代附加服务 el-popover
.native-popover {
  position: relative;
  z-index: 20;
  width: 100%;
}

// 鼠标移入附加服务后展示不裁切的完整内容
.service-popover-content {
  position: absolute;
  left: calc(-280px);
  top: 50%;
  z-index: 99 !important;
  display: none;
  width: 250px;
  transform: translateY(-50%);
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  color: #303133;
  font-size: 14px;

  // 浮层服务项保持与列表一致的行距
  p {
    margin: 0;
    line-height: 1.6;
  }
}

.native-popover:hover .service-popover-content {
  display: block;
}

// 标签悬浮层：单元格内保留紧凑展示，鼠标移入后显示完整内容
.tag-cell {
  position: relative;
  z-index: 10;
  overflow: visible !important;
}

.native-tag-popover {
  position: relative;
  z-index: 20;
  width: 100%;
}

.tag-list {
  width: 100%;
  max-height: 150px;
  overflow: hidden;
}

.tag-popover-content {
  position: absolute;
  left: calc(100% + 8px);
  top: 0;
  z-index: 30;
  display: none;
  width: 200px;
  max-width: 60vw;
  padding: 10px 12px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  color: #303133;
  font-size: 14px;
  box-sizing: border-box;
}

.native-tag-popover:hover .tag-popover-content {
  display: block;
}

// 附加服务浮层经过标签列时，不同时弹出标签浮层，避免两个完整内容重叠
.service-cell:hover~.tag-cell .tag-popover-content {
  display: none;
}

// 商品行下方的辅料卡片模块
.warehouse-material-panel {
  width: 100%;
  min-width: 0;
  overflow: hidden;
}

// 标签信息单元格
.tag-cell {
  flex-direction: column;
  align-items: flex-start !important;
  gap: 7px;
}

// 单个商品标签
.tag-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-bottom: 6px;
  border-bottom: 1px dashed #dcdfe6;
}

// 数量列
.quantity-cell {
  color: #303133;
  font-size: 14px;
  font-weight: 600;
}

// 可提出数列中的出货数上下排列
.available-quantity-cell {
  flex-direction: column;
  gap: 8px;
}

// 可提出数可点击打开库位查看
.available-quantity-value {
  color: #b4272d;
  cursor: pointer;
}

.available-quantity-value:hover {
  text-decoration: underline;
}

// 出货数输入框保持紧凑宽度并居中显示
.shipment-quantity-input {
  width: 100%;
  height: 28px;
  padding: 0 4px;
  text-align: center;
}

// 空列表提示
// 配送单生成结果内容和可点击配送单号样式
.native-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
}

.native-dialog {
  width: 420px;
  border-radius: 2px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  box-sizing: border-box;
}

.native-dialog-header {
  position: relative;
  padding: 18px 20px 12px;
  text-align: left;
}

.native-dialog-title {
  color: #303133;
  font-size: 18px;
  font-weight: 400;
}

.native-dialog-close {
  position: absolute;
  top: 14px;
  right: 14px;
  border: 0;
  background: transparent;
  color: #909399;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
}

.box-delivery-result {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 42px;
  padding: 12px 20px 24px;
  font-size: 14px;
}

.box-delivery-link {
  color: #c7252b;
  text-decoration: underline;
  cursor: pointer;
}

.empty-list,
.pending-list {
  padding: 70px 20px;
  color: #909399;
  text-align: center;
}
</style>
